import React from 'react';

import {
  Checkbox,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import AppointmentCoachRoleAutoComplete, {
  AppointmentCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentCoachRoleAutoComplete';
import AppointmentTypeAutoComplete, {
  AppointmentTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentTypeAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs, { Dayjs } from 'dayjs';
import { RRuleSet, rrulestr } from 'rrule';
import { AppointmentRetrieve, DayAbbreviation } from 'types';
import { TimeUnitEnum } from 'types/enums';
import {
  calculateEventEndDateTime,
  getDayAbbreviationFromDate,
  getDayAbbreviationsFromRruleBydays,
  getDayDiff,
  parseRruleForFrequency,
} from 'utils/event.helpers';

interface FormValues {
  appointmentId: string;
  startDate: Date;
  startTime: string;
  duration: number;
  endDate: Date | null;
  appointmentType: AppointmentTypeAutoCompleteValue;
  appointmentCoachAppointmentCoachRoles: {
    appointmentCoachAppointmentCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    appointmentCoachRole: AppointmentCoachRoleAutoCompleteValue;
  }[];
  location: LocationAutoCompleteValue;
  rootAppointment: string | null;
  days: DayAbbreviation[];
  isRecurring: boolean;
  appointmentEnd: Dayjs | null;
  dayDiff: string;
  cancellationSettings: {
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
  };
}

interface IDetailsViewProps {
  appointment: AppointmentRetrieve;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const DetailsView = ({ appointment }: IDetailsViewProps): JSX.Element => {
  const { country } = useLocalization();

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();

  const initialDays = appointment.rootAppointment?.rrule
    ? getDayAbbreviationsFromRruleBydays(
        (rrulestr(appointment.rootAppointment.rrule, { forceset: true }) as RRuleSet)._rrule[0].options
          .byweekday,
      )
    : [getDayAbbreviationFromDate(dayjs(appointment.startDate).toDate())];

  const isMonthlyRecurrence = parseRruleForFrequency(appointment.rootAppointment?.rrule) === 'monthly';

  const cancellationSettings = appointment.cancellationSettings;

  const initialappointmentEnd = calculateEventEndDateTime(
    appointment.duration,
    appointment.startTime,
    dayjs(appointment.startDate).toDate(),
  );

  const formValues: FormValues = {
    ...appointment,
    startDate: dayjs(appointment.startDate).toDate(),
    startTime: dayjs(`${appointment.startDate} ${appointment.startTime}`).format('HH:mm').toString(),
    endDate: appointment.rootAppointment?.endDate
      ? dayjs(appointment.rootAppointment.endDate).toDate()
      : null,
    appointmentType: {
      appointmentTypeId: appointment.appointmentType?.appointmentTypeId || '',
      value: appointment.appointmentType?.appointmentTypeId || '',
      label: appointment.appointmentType?.name || '',
    },
    appointmentCoachAppointmentCoachRoles: appointment.appointmentCoachAppointmentCoachRoles.map(acacr => ({
      appointmentCoachAppointmentCoachRoleId: acacr.appointmentCoachAppointmentCoachRoleId,
      coach: {
        staffId: acacr.coach.staffId,
        value: acacr.coach.staffId,
        label: acacr.coach.fullName,
      },
      appointmentCoachRole: {
        appointmentCoachRoleId: acacr.appointmentCoachRole.appointmentCoachRoleId,
        value: acacr.appointmentCoachRole.appointmentCoachRoleId,
        label: acacr.appointmentCoachRole.name,
      },
    })),
    location: {
      locationId: appointment.location?.locationId || '',
      label: appointment.location?.name || '',
      value: appointment.location?.locationId || '',
    },
    rootAppointment: appointment.rootAppointment?.appointmentId || null,
    days: initialDays,
    isRecurring: !!appointment.rrule,
    cancellationSettings: {
      allowLateCancellation: cancellationSettings.allowLateCancellation ?? true,
      cancellationInterval: cancellationSettings.cancellationInterval ?? 'minute',
      cancellationIntervalCount: cancellationSettings.cancellationIntervalCount ?? 0,
      cancellationFee: cancellationSettings.cancellationFee ?? 0,
      cancellationCreditDeduction: cancellationSettings.cancellationCreditDeduction ?? false,
      noShowFee: cancellationSettings.noShowFee ?? 0,
      noShowCreditDeduction: cancellationSettings.noShowCreditDeduction ?? false,
    },
    appointmentEnd: initialappointmentEnd,
    dayDiff: getDayDiff(dayjs(appointment.startDate), initialappointmentEnd),
  };

  const form = useForm<FormValues>({
    initialValues: formValues,
  });

  useShallowEffect(() => {
    form.setValues(formValues);
  }, [appointment]);

  return (
    <Stack spacing='xs'>
      <Group grow align='top'>
        <AppointmentTypeAutoComplete
          data-cy='appointInput'
          label='Appointment Type'
          placeholder='Select a appointment type'
          value={form.values.appointmentType}
          onChange={() => {}}
          disabled
        />
        <LocationAutoComplete
          data-cy='locationAppointInput'
          label='Location'
          placeholder='Select a location'
          value={form.values.location}
          onChange={() => {}}
          disabled
        />
      </Group>

      <Paper withBorder p='sm' bg='gray.0'>
        <Stack spacing='sm'>
          {!form.values.appointmentCoachAppointmentCoachRoles.length && (
            <Text size='sm' color='dimmed'>
              No Coach
            </Text>
          )}
          {form.values.appointmentCoachAppointmentCoachRoles.map((acacr, i) => (
            <Group
              key={acacr.appointmentCoachAppointmentCoachRoleId}
              position='apart'
              align='flex-start'
              noWrap
            >
              <StaffAutoComplete
                {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.coach`)}
                label='Coach'
                placeholder='Select a coach'
                disinclude={form.values.appointmentCoachAppointmentCoachRoles.map(a => a.coach.staffId)}
                isCoach
                disabled
              />
              <AppointmentCoachRoleAutoComplete
                {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.appointmentCoachRole`)}
                label='Role'
                placeholder='Select a role'
                disabled
              />
              <div />
            </Group>
          ))}
        </Stack>
      </Paper>

      <Divider mt='xs' mx='-xl' />

      <Group grow align='top'>
        <NumberInput
          {...form.getInputProps('duration')}
          data-cy='durationAppointInput'
          label='Duration in minutes'
          placeholder='Enter duration'
          disabled
        />
        <div />
      </Group>

      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <DateInput {...form.getInputProps('startDate')} label='Start Date' locale='us' disabled />
        <Group spacing='xs' grow>
          <TimeInput
            {...form.getInputProps('startTime')}
            data-cy='timeFromAppointInput'
            label='From'
            disabled
          />
          <TimeInput
            label={
              <Group position='apart'>
                <div>To</div>
                <Text color='red'>{form.values.dayDiff}</Text>
              </Group>
            }
            value={form.values.appointmentEnd ? form.values.appointmentEnd.format('HH:mm') : ''}
            disabled
          />
        </Group>
      </SimpleGrid>
      <Group pt='xs'>
        <Stack>
          <Group>
            <Checkbox checked={form.values.isRecurring} label='Re-occurring Event' disabled />
          </Group>

          <Stack pl='md'>
            {isMonthlyRecurrence ? (
              <Input.Wrapper label='Recurrence'>
                <Text size='sm'>Monthly — same date each calendar month</Text>
              </Input.Wrapper>
            ) : (
              <Checkbox.Group label='Weekly' {...form.getInputProps('days')}>
                <Group spacing='lg'>
                  <Checkbox data-test='sunday' value='SU' label='Sun' disabled />
                  <Checkbox data-test='monday' value='MO' label='Mon' disabled />
                  <Checkbox data-test='tuesday' value='TU' label='Tues' disabled />
                  <Checkbox data-test='wednesday' value='WE' label='Wed' disabled />
                  <Checkbox data-test='thursday' value='TH' label='Thu' disabled />
                  <Checkbox data-test='friday' value='FR' label='Fri' disabled />
                  <Checkbox data-test='saturday' value='SA' label='Sat' disabled />
                </Group>
              </Checkbox.Group>
            )}
            <Group grow align='top'>
              <DateInput
                {...form.getInputProps('endDate')}
                label='End Date'
                placeholder={form.values.isRecurring ? 'Never' : 'N/A'}
                data-test='endDate'
                disabled
              />
              <div />
            </Group>
          </Stack>
        </Stack>
      </Group>

      <Divider mt='lg' mb={0} mx='-xl' />

      <ExpandableSection
        title='Additional Settings'
        opened={settingsOpened}
        handleToggle={handleSettingsToggle}
      >
        <Stack spacing='sm' px='md'>
          <Input.Wrapper>
            <SimpleGrid cols={1}>
              <Switch
                disabled
                my='xs'
                label='Allow Late Cancellation'
                checked={form.values?.cancellationSettings?.allowLateCancellation || false}
                {...form.getInputProps('cancellationSettings.allowLateCancellation')}
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper
            label={
              form.values.cancellationSettings.allowLateCancellation
                ? 'Time before the start of an appointment when a cancellation is considered late'
                : 'Time before the start of an appointment when cancellations are no longer allowed'
            }
          >
            <SimpleGrid cols={2}>
              <NumberInput
                disabled
                min={0}
                {...form.getInputProps('cancellationSettings.cancellationIntervalCount')}
              />
              <Select
                disabled
                data={timeUnitOptions}
                {...form.getInputProps('cancellationSettings.cancellationInterval')}
              />
            </SimpleGrid>
          </Input.Wrapper>
          {form.values.cancellationSettings.allowLateCancellation && (
            <>
              <Input.Wrapper label='Auto-Charge Fee for Late cancellations'>
                <SimpleGrid cols={2}>
                  <MoneyField
                    country={country}
                    min={0}
                    disabled
                    {...form.getInputProps('cancellationSettings.cancellationFee')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    disabled
                    my='xs'
                    label='Deduction of credit for late cancellations'
                    checked={form.values?.cancellationSettings?.cancellationCreditDeduction || false}
                    {...form.getInputProps('cancellationSettings.cancellationCreditDeduction')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
            </>
          )}

          <Input.Wrapper label='Auto-Charge Fee for No Shows'>
            <SimpleGrid cols={2}>
              <MoneyField
                country={country}
                min={0}
                disabled
                {...form.getInputProps('cancellationSettings.noShowFee')}
              />
            </SimpleGrid>
          </Input.Wrapper>
          <Input.Wrapper>
            <SimpleGrid cols={1}>
              <Switch
                my='xs'
                label='Deduction of credit for No Shows'
                disabled
                checked={form.values?.cancellationSettings?.noShowCreditDeduction || false}
                {...form.getInputProps('cancellationSettings.noShowCreditDeduction')}
              />
            </SimpleGrid>
          </Input.Wrapper>
        </Stack>
      </ExpandableSection>
    </Stack>
  );
};

export default DetailsView;
